/**
 * Lane C0-FACE (lab only). The proposed face's small parts as pixel stamps (pure: no canvas): nose, mouth (rest
 * and expression mouths), marks, and the age and cheek cues. The eyes and brows are fine.ts's traced sprites.
 */
import type { View } from '../../sprites';
import { byId, type Expr, MARKS, MOUTHS, type MouthShape, NOSES, type Sex } from './parts';
import type { LabFace } from './draw';
import { mirrorRows } from '../leftArt';

type Grid = string[];

export interface Placed {
  x: number;
  y: number;
  rows: Grid;
}

/** The mouth for an expression, head-on, centred on the face's middle line. */
export function mouthRows(m: MouthShape, expr: Expr, sex: Sex): Grid {
  const w = m.rows[0]!.length;
  const lip = m.lips ? 'L' : 'm';
  const W = (n: number) => Math.max(n, w);
  const row = (n: number, ends: string, fill: string) => ends + fill.repeat(Math.max(0, n - 2)) + ends;
  switch (expr) {
    case 'smile': {
      const n = W(4);
      return [row(n, 'm', '.'), '.' + 'm'.repeat(n - 2) + '.'];
    }
    case 'talk':
      return ['.mm.', 'mMMm', `.${lip}${lip}.`];
    case 'angry': {
      const n = W(4);
      return [row(n, 'n', 'm'), row(n, 'm', 't')];
    }
    case 'shout': {
      const n = sex === 'female' ? 4 : 5;
      return ['.' + 'm'.repeat(n - 2) + '.', row(n, 'm', 't'), row(n, 'm', 'M'), row(n, 'm', 'M'), '.' + lip.repeat(n - 2) + '.'];
    }
    case 'hurt': {
      const n = W(5) + 1;
      return ['.' + 'm'.repeat(n - 2) + '.', row(n, 'm', 't'), row(n, 'm', '.')];
    }
    default:
      return [...m.rows];
  }
}

const mirror = (g: Grid) => g.map((r) => [...r].reverse().join(''));

const MOUTH_AT = { front: { x: 0, y: 15 }, front3: { x: 5, y: 15 }, side: { x: 13, y: 15 } } as const;

export function mouthPlaced(view: View, rows: Grid): Placed | undefined {
  const w = rows[0]!.length;
  const lift = rows.length > 2 ? 1 : 0;
  if (view === 'front') return { x: -Math.floor(w / 2), y: MOUTH_AT.front.y - lift, rows };
  if (view === 'front3') {
    const r = w > 2 ? rows.map((s) => s.slice(0, -1)) : rows;
    return { x: MOUTH_AT.front3.x - Math.floor(r[0]!.length / 2), y: MOUTH_AT.front3.y - lift, rows: r };
  }
  if (view === 'side') {
    const r = rows.map((s) => s.slice(Math.ceil(w / 2)).slice(0, 2));
    return { x: MOUTH_AT.side.x, y: MOUTH_AT.side.y - lift, rows: r };
  }
  return undefined;
}

/**
 * Nose and mouth for a view and expression (the mouth hidden by a long beard unless it opens). On a left-facing
 * view (`left`, rig/leftArt.ts) a one-sided mouth (a smirk) is the mirror image's.
 */
export function featureStamps(f: LabFace, view: View, expr: Expr, left = false): Placed[] {
  if (view === 'back' || view === 'back3') return [];
  const out: Placed[] = [];
  const nose = byId(NOSES, f.nose).views[view];
  if (nose) out.push({ ...nose, rows: [...nose.rows] });
  const covered =
    (f.beard === 'long' || f.beard === 'full' || f.beard === 'wizard' || f.mustache === 'walrus') &&
    !['shout', 'talk', 'hurt'].includes(expr);
  if (!covered) {
    const rows = mouthRows(byId(MOUTHS, f.mouth), expr, f.sex);
    const mp = mouthPlaced(view, left ? mirrorRows(rows) : rows);
    if (mp) out.push(mp);
  }
  return out;
}

/** The traced eyes' bottom row (head2.ts REF_FACE) per view: cues go under it. */
const EYE_BOTTOM = { front: 10, front3: 10, side: 9 } as const;

/** Age cues, cheeks and marks: bags, crow's feet and folds for elders, smile lines, blush, scars, moles. */
export function cueStamps(f: LabFace, view: View, expr: Expr, left = false): Placed[] {
  if (view === 'back' || view === 'back3') return [];
  const out: Placed[] = [];
  const bags = f.age === 'elder';
  const folds = f.age === 'elder' || f.lines;
  const low = EYE_BOTTOM[view];
  if (view === 'front') {
    const both = (x: number, y: number, rows: Grid) => {
      out.push({ x, y, rows });
      out.push({ x: -1 - (x + rows[0]!.length - 1), y, rows: mirror(rows) });
    };
    if (bags && expr !== 'blink') both(5, low + 1, ['.ccc']);
    if (bags) both(12, 5, ['c', '.', 'c']);
    if (folds) both(2, 12, ['c.', 'c.', '.c']);
    if (f.blush && f.style !== 'old') both(6, low + 2, ['BBB']);
  } else if (view === 'front3') {
    if (bags && expr !== 'blink') out.push({ x: -4, y: low + 1, rows: ['ccc'] });
    if (folds) out.push({ x: 3, y: 12, rows: ['.c', '.c', 'c.'] }, { x: 9, y: 12, rows: ['c', 'c'] });
    if (f.blush && f.style !== 'old') out.push({ x: -6, y: low + 2, rows: ['BBB'] }, { x: 10, y: low + 2, rows: ['BB'] });
  } else if (view === 'side') {
    if (bags && expr !== 'blink') out.push({ x: 8, y: low + 1, rows: ['cc'] });
    if (folds) out.push({ x: 12, y: 13, rows: ['c', 'c'] });
    if (f.blush && f.style !== 'old') out.push({ x: 8, y: 11, rows: ['BBB'] });
  }
  // A one-sided mark on a left-facing view: where the mirror image has it (rig/leftArt.ts).
  for (const m of f.marks ?? []) {
    const mark = byId(MARKS, m);
    for (const st of (left && mark.left?.[view]) || mark.views[view] || []) out.push({ ...st, rows: [...st.rows] });
  }
  return out;
}
